import { Box, Button, Checkbox, Sheet } from "@mui/joy";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
} from "@mui/x-data-grid";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "../../../../i18n";
import {
  InspectionDetailsByRefDocument,
  InspectionLotInput,
  useInspectionDetailsByRefQuery,
  useInspectionDetailsUpdateMutation,
} from "../../../../__generated__/types-and-hooks";
import { useParams } from "react-router-dom";
import BulkEditSkuModal from "./BulkEditSkuModal";
// import CheckCircleOutline from "@mui/icons-material/Check";
// import CancelOutlined from "@mui/icons-material/Close";
import SkusTableModal from "./SkusTable";
import { useTeam } from "../../TeamLayout";
import SkuRef from "../../../../components/SkuRef";

const integerColumn = {
  type: "number",
  // valueFormatter: (params: any) => `${Math.floor(params.value)}`,
} as const;

const JoyCheckbox = React.forwardRef(function JoyCheckbox(props: any, ref) {
  const { indeterminate, checked, ...other } = props;
  return (
    <Checkbox
      ref={ref}
      indeterminate={indeterminate}
      checked={checked}
      variant="outlined"
      color="neutral"
      {...other}
    />
  );
});

// function renderAQL(params: any) {
//   const aqlData = params.value;
//   if (!aqlData) return "";
//   return (
//     <Stack style={{ lineHeight: 1.2 }} direction="row" spacing={1}>
//       <Chip
//         startDecorator={<Typography sx={{ fontSize: "8px" }}>AQL</Typography>}
//         color="primary"
//         sx={{ borderRadius: 4 }}
//       >
//         {aqlData.aql}
//       </Chip>
//       <Chip
//         startDecorator={<CheckCircleOutline sx={{ fontSize: "12px" }} />}
//         color="success"
//         sx={{ borderRadius: 4 }}
//       >
//         {aqlData.acceptanceNumber}
//       </Chip>
//       <Chip
//         startDecorator={<CancelOutlined sx={{ fontSize: "12px" }} />}
//         color="danger"
//         sx={{ borderRadius: 4 }}
//       >
//         {aqlData.rejectionNumber}
//       </Chip>
//     </Stack>
//   );
// }

export default function SKUs() {
  const [modal, setModal] = useState<string | null>(null);
  const teamCtx = useTeam();

  const { ref } = useParams();
  const inspectionQuery = useInspectionDetailsByRefQuery({
    variables: { uniqueId: ref! },
  });
  const inspection = inspectionQuery.data?.inspection_by_unique_id;
  const t = useTranslation();
  const RefRenderCell = useCallback(
    (params: GridRenderCellParams<any, string>) => {
      return (
        <SkuRef
          uniqueId={params.row.skuUniqueId}
          teamSlug={teamCtx.team.slug}
        />
      );
    },
    [teamCtx.team]
  );
  const [selectionModel, setSelectionModel] = React.useState<number[]>([]);
  const [inspectionDetailsUpdateMutation] =
    useInspectionDetailsUpdateMutation();

  const handleDeleteSku = useCallback(() => {
    if (!inspection) return;

    inspectionDetailsUpdateMutation({
      variables: {
        updates: {
          uniqueId: inspection.uniqueId,
          inspectionLotIdsToDelete: selectionModel,
        },
      },
      update: (cache, { data }) => {
        const updatedInspection = data?.inspection_update;
        if (!updatedInspection) return;

        // Manually write updated inspection into the cache
        cache.writeQuery({
          query: InspectionDetailsByRefDocument,
          variables: { uniqueId: inspection.uniqueId },
          data: {
            inspection_by_unique_id: updatedInspection,
          },
        });
      },
    });
  }, [selectionModel, inspection, inspectionDetailsUpdateMutation]);

  const handleAddSku = useCallback(() => {
    setModal("add sku");
  }, []);

  const handleBulkEditSku = useCallback(() => {
    setModal("bulk edit sku");
  }, []);

  const handleSaveBulkSku = useCallback(
    (inspectionLots: InspectionLotInput[]) => {
      setModal(null);

      if (!inspection) return;
      inspectionDetailsUpdateMutation({
        variables: {
          updates: {
            uniqueId: inspection.uniqueId,
            inspectionLots: inspectionLots,
          },
        },
        update: (cache, { data }) => {
          const updatedInspection = data?.inspection_update;
          if (!updatedInspection) return;

          // Manually write updated inspection into the cache
          cache.writeQuery({
            query: InspectionDetailsByRefDocument,
            variables: { uniqueId: inspection.uniqueId },
            data: {
              inspection_by_unique_id: updatedInspection,
            },
          });
        },
      });
    },
    [inspection, inspectionDetailsUpdateMutation]
  );

  const handleSaveSku = useCallback(
    (inspectionLots: InspectionLotInput[]) => {
      setModal(null);

      if (!inspection || !inspectionLots) return;

      inspectionDetailsUpdateMutation({
        variables: {
          updates: {
            uniqueId: inspection.uniqueId,
            inspectionLots: inspectionLots,
          },
        },
        update: (cache, { data }) => {
          const updatedInspection = data?.inspection_update;
          if (!updatedInspection) return;

          // Manually write updated inspection into the cache
          cache.writeQuery({
            query: InspectionDetailsByRefDocument,
            variables: { uniqueId: inspection.uniqueId },
            data: {
              inspection_by_unique_id: updatedInspection,
            },
          });
        },
      });
    },
    [inspection, inspectionDetailsUpdateMutation]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "skuUniqueId",
        headerName: "ID",
        width: 80,
        editable: false,
        renderCell: RefRenderCell,
      },
      {
        field: "sku",
        headerName: "SKU",
        width: 240,
        editable: false,
      },
      {
        field: "name",
        headerName: "Name",
        width: 240,
        editable: false,
        valueGetter: (params: any, row) => row.skuObj?.name,
      },
      {
        ...integerColumn,
        field: "lotSize",
        width: 80,
        editable: true,
        renderHeader: () => (
          <>
            Lot
            <br />
            Size
          </>
        ),
      },
      {
        field: "inspectionLevel",
        type: "singleSelect",
        renderHeader: () => (
          <>
            Inspection
            <br />
            Level
          </>
        ),
        width: 80,
        editable: true,
        valueOptions: ["GI", "GII", "GIII", "S1", "S2", "S3", "S4"],
      },
      {
        ...integerColumn,
        field: "sampleSize",
        renderHeader: () => (
          <>
            Sample
            <br />
            Size
          </>
        ),
        width: 80,
        editable: true,
      },
      {
        field: "codeLetter",
        renderHeader: () => (
          <>
            Code
            <br />
            Letter
          </>
        ),
        width: 80,
        editable: false,
      },
      // {
      //   field: "criticalAql",
      //   renderHeader: () => (
      //     <Stack direction="column">
      //       Critical Defect Limits
      //       <Stack direction="row" spacing={1}>
      //         <Typography fontSize="sm">AQL</Typography>
      //         <Typography fontSize="sm">Accept</Typography>
      //         <Typography fontSize="sm">Reject</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      //   width: 180,
      //   editable: false,
      //   valueGetter: (params: any, row) => row.criticalAql,
      //   renderCell: renderAQL,
      // },
      {
        field: "criticalDefects",
        ...integerColumn,
        renderHeader: () => (
          <>
            Critical
            <br />
            Defects
          </>
        ),
        editable: true,
        width: 80,
      },
      // {
      //   field: "majorAql",
      //   headerName: "Major AQL",
      //   renderHeader: () => (
      //     <Stack direction="column">
      //       Major Defect Limits
      //       <Stack direction="row" spacing={1}>
      //         <Typography fontSize="sm">AQL</Typography>
      //         <Typography fontSize="sm">Accept</Typography>
      //         <Typography fontSize="sm">Reject</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      //   width: 180,
      //   editable: false,
      //   valueGetter: (params: any, row) => row.majorAql,
      //   renderCell: renderAQL,
      // },
      {
        field: "majorDefects",
        ...integerColumn,
        renderHeader: () => (
          <>
            Major
            <br />
            Defects
          </>
        ),
        width: 80,
        editable: true,
      },
      // {
      //   field: "minorAql",
      //   headerName: "Minor AQL",
      //   renderHeader: () => (
      //     <Stack direction="column">
      //       Minor Defect Limits
      //       <Stack direction="row" spacing={1}>
      //         <Typography fontSize="sm">AQL</Typography>
      //         <Typography fontSize="sm">Accept</Typography>
      //         <Typography fontSize="sm">Reject</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      //   width: 180,
      //   editable: false,
      //   valueGetter: (params: any, row) => row.minorAql,
      //   renderCell: renderAQL,
      // },
      {
        field: "minorDefects",
        ...integerColumn,
        renderHeader: () => (
          <>
            Minor
            <br />
            Defects
          </>
        ),
        width: 80,
        editable: true,
      },
    ],
    [RefRenderCell]
  );

  const handleProcessRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow: GridRowModel,
      { rowId }: { rowId: GridRowId }
    ) => {
      if (!inspection) return;
      const inspectionLot = inspection?.inspectionLots.find(
        (lot) => lot.id === rowId
      );

      if (inspectionLot) {
        inspectionDetailsUpdateMutation({
          variables: {
            updates: {
              uniqueId: inspection.uniqueId,
              inspectionLots: [
                {
                  skuUniqueId: inspectionLot.skuUniqueId,
                  lotSize: newRow.lotSize,
                  inspectionLevel: newRow.inspectionLevel,
                  criticalDefects: newRow.criticalDefects,
                  majorDefects: newRow.majorDefects,
                  minorDefects: newRow.minorDefects,
                },
              ],
            },
          },
          update: (cache, { data }) => {
            const updatedInspection = data?.inspection_update;
            if (!updatedInspection) return;

            // Manually write updated inspection into the cache
            cache.writeQuery({
              query: InspectionDetailsByRefDocument,
              variables: { uniqueId: inspection.uniqueId },
              data: {
                inspection_by_unique_id: updatedInspection,
              },
            });
          },
        });
      }
      return newRow;
    },
    [inspectionDetailsUpdateMutation, inspection]
  );

  return (
    <>
      <Box sx={{ display: "flex", p: 2, pb: 0 }} gap={1}>
        <Button variant="outlined" onClick={handleAddSku}>
          {t("Add")}
        </Button>
        <Button
          disabled={selectionModel.length === 0}
          variant="outlined"
          onClick={handleBulkEditSku}
        >
          {t("Bulk Edit")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          disabled={selectionModel.length === 0}
          variant="solid"
          color="danger"
          onClick={handleDeleteSku}
        >
          {t("Remove")}
        </Button>
      </Box>
      <Box
        sx={{
          gap: 2,
          p: 2,
          display: "flex",
          flex: 1,
        }}
      >
        <Sheet sx={{ width: "100%", overflow: "auto" }}>
          {inspection && (
            <DataGrid
              checkboxSelection
              sx={{ backgroundColor: "var(--joy-palette-background-body)" }}
              rows={inspection.inspectionLots}
              columns={columns}
              editMode="cell"
              showColumnVerticalBorder
              showCellVerticalBorder
              rowHeight={28}
              disableVirtualization={true}
              disableColumnMenu
              disableColumnFilter
              disableRowSelectionOnClick
              processRowUpdate={handleProcessRowUpdate}
              slots={{
                baseCheckbox: JoyCheckbox,
              }}
              onRowSelectionModelChange={(newSelection) => {
                setSelectionModel(newSelection as number[]);
              }}
            />
          )}
        </Sheet>
      </Box>
      <SkusTableModal
        team={teamCtx.team}
        open={modal === "add sku"}
        onClose={() => setModal(null)}
        onSave={handleSaveSku}
      />
      <BulkEditSkuModal
        open={modal === "bulk edit sku"}
        inspectionLots={
          inspection?.inspectionLots
            .filter((lot) => selectionModel.includes(lot.id))
            .map((lot) => ({
              sku: lot.sku,
              skuUniqueId: lot.skuUniqueId,
            })) ?? []
        }
        onClose={() => setModal(null)}
        onSave={handleSaveBulkSku}
      />
    </>
  );
}
